import { useContext, useEffect, useRef, useState } from "react";
import { makeStyles } from "@mui/styles";
import { Box } from "@mui/material";
import Typography from "@mui/material/Typography";
import { ReactComponent as StreakStarFilled } from "../../../assets/StreakStarFilled.svg";
import StreakCompleteAnimation from "../../../assets/StreakConfettiAnimation.json";
import StreakUnfilledAnimation from "../../../assets/streakUnfilledAnimation.json";
import Lottie, { LottieRefCurrentProps } from "lottie-react";
import { TempEventContext } from "../../../context/TempEventContext";
import { TempEventCtx } from "../../../interfaces";

const ANIMATIONDELAY = 400;

const useStyle = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
  },
  overviewSection: {
    display: "flex",
    flexDirection: "column",
    maxWidth: "45.625rem",
    alignSelf: "center",
    marginBottom: "3rem",
  },
  overviewCard: {
    display: "flex",
    color: "#FFFFFF",
    backgroundImage: "linear-gradient(to right, #8C1D40, #802642)",
    borderRadius: "0.625rem",
    boxShadow: "0px 0.2rem 1.25rem #00000015",
  },
  starBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 11,
    position: "relative",
  },
  filled: {
    backgroundColor: theme.palette.secondary.main,
  },
  unfilled: {
    backgroundColor: "#F2F2F2",
  },
  streakCompleteAnimation: {
    position: "absolute",
    width: "96px",
    height: "96px",
  },
  streakInCompleteAnimation: {
    position: "absolute",
    top: "-13px",
    width: "75px",
    height: "75px",
    right: "-13px",
  },
  fillCircle: {
    width: "100%",
    height: "100%",
    backgroundColor: theme.palette.secondary.main,
    borderRadius: "50%",
    position: "absolute",
  },
  fillCircleAnimated: {
    backgroundColor: theme.palette.secondary.main,
    borderRadius: "50%",
    position: "absolute",
    animation: `$fillEffect ${ANIMATIONDELAY}ms ${theme.transitions.easing.easeInOut}`,
    "-webkit-animation-fill-mode": "forwards",
  },
  "@keyframes fillEffect": {
    "0%": {
      width: 0,
      height: 0,
    },
    "100%": {
      width: "100%",
      height: "100%",
    },
  },
  starColor: {
    color: "#FFFFFF",
  },
  starColorAnimated: {
    animation: `$starFillEffect ${ANIMATIONDELAY}ms ${theme.transitions.easing.easeInOut}`,
    "-webkit-animation-fill-mode": "forwards",
  },
  "@keyframes starFillEffect": {
    "0%": {
      color: "#000000",
      opacity: 0.2,
    },
    "100%": {
      color: "#FFFFFF",
      opacity: 1,
    },
  },
}));

interface StreakStarSize {
  center: number;
  default: number;
}

interface StreakStarData {
  day: string;
  isFilled: boolean;
  isCenter: boolean;
  circleSize?: StreakStarSize;
  iconSize?: StreakStarSize;
  animateOverride?: boolean;
}

export const SingleStreakStar = (props: StreakStarData) => {
  const classes = useStyle();
  const {
    day,
    isFilled,
    isCenter,
    circleSize = { center: 48, default: 32 },
    iconSize = { center: 24, default: 16 },
    animateOverride,
  } = props;
  const [canPlay, setCanPlay] = useState(false);
  const tempEventCtx = useContext(TempEventContext) as TempEventCtx;

  const [isStreakComplete, setIsStreakComplete] = useState(false);
  const streakCompleteAnimationRef = useRef<LottieRefCurrentProps>(null);

  if (isFilled && isCenter && !isStreakComplete) {
    setIsStreakComplete(true);
  }

  useEffect(() => {
    const check =
      (!tempEventCtx.showStreakModal && !animateOverride) ||
      (tempEventCtx.showStreakModal && animateOverride);
    setCanPlay(check ?? false);
  }, [tempEventCtx.showStreakModal, animateOverride]);

  useEffect(() => {
    if (
      isStreakComplete &&
      streakCompleteAnimationRef?.current &&
      (tempEventCtx.showStreakAnimation || animateOverride) &&
      canPlay
    ) {
      // delay playback until fill is about complete
      setTimeout(
        (ref) => {
          ref.play();
        },
        ANIMATIONDELAY + 100,
        streakCompleteAnimationRef?.current
      );
    }
  }, [
    isStreakComplete,
    streakCompleteAnimationRef,
    tempEventCtx.showStreakAnimation,
    animateOverride,
    tempEventCtx.showStreakModal,
    canPlay,
  ]);

  const handleOnAnimationEnd = () => {
    if (!animateOverride) {
      tempEventCtx.setShowStreakAnimation(false);
    }
  };
  //${isCenter && !isFilled ? classes.rippleAnimation : ""}
  return (
    <Box>
      <Box className={classes.starBox} sx={{ height: circleSize.center }}>
        {isCenter && !isFilled && (
          <Lottie
            className={classes.streakInCompleteAnimation}
            animationData={StreakUnfilledAnimation}
            loop={true}
            autoplay={true}
          />
        )}
        <Box
          className={`${isFilled && !isCenter ? classes.filled : classes.unfilled}`}
          sx={{
            position: "relative",
            width: isCenter ? `${circleSize.center}px` : `${circleSize.default}px`,
            height: isCenter ? `${circleSize.center}px` : `${circleSize.default}px`,
            borderRadius: "50%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}>
          {/* fill effect */}
          {isCenter && isFilled && canPlay && (
            <Box
              className={
                !tempEventCtx.showStreakAnimation && !animateOverride
                  ? classes.fillCircle
                  : classes.fillCircleAnimated
              }
            />
          )}

          <StreakStarFilled
            style={{
              width: !isCenter ? `${iconSize.default}px` : `${iconSize.center}px`,
              height: !isCenter ? `${iconSize.default}px` : `${iconSize.center}px`,
              color: !isFilled ? "#000000" : "#FFFFFF",
              opacity: !isFilled ? 0.2 : "1",
              zIndex: 0,
            }}
            className={
              isCenter &&
              isFilled &&
              (tempEventCtx.showStreakAnimation || animateOverride)
                ? classes.starColorAnimated
                : classes.starColor
            }
          />

          <Lottie
            lottieRef={streakCompleteAnimationRef}
            className={classes.streakCompleteAnimation}
            animationData={StreakCompleteAnimation}
            loop={false}
            autoplay={false}
            onComplete={handleOnAnimationEnd}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
        }}>
        <Typography sx={{ fontSize: "1em", fontWeight: "bold" }}>{day}</Typography>
      </Box>
    </Box>
  );
};
